// Game sounds, the same ones as the ad: a tick as each letter lands on a tile, on Enter a clack
// plus, when the word burns letters, a whoosh and crackle, and the phoenix rising after Raise. On
// Results, the score lands (ticks, a clack per rank, a stamp) and the achievement reveal plays (the
// sheet, the fuse, clinks up the ring, a tier up).
// They're synthesized, not recorded: design/game-sounds renders these WAVs from code, and the ad
// mixes the same recipes. The one exception is Ablaze, which layers two recordings.
import ablazeCrackleUrl from '../sounds/ablaze-crackle.wav'
import ablazeFireUrl from '../sounds/ablaze-fire.wav'
import burnUrl from '../sounds/burn.wav'
import clackUrl from '../sounds/clack.wav'
import clink1100Url from '../sounds/clink-1100.wav'
import clink2200Url from '../sounds/clink-2200.wav'
import clink4400Url from '../sounds/clink-4400.wav'
import flareUrl from '../sounds/flare.wav'
import fuseUrl from '../sounds/fuse.wav'
import { COUNT_TICK, FUSE_RUN, LEVELS } from '../sounds/levels.ts'
import phoenixUrl from '../sounds/phoenix.wav'
import sheetUrl from '../sounds/sheet.wav'
import stampUrl from '../sounds/stamp.wav'
import tickUrl from '../sounds/tick.wav'
import { storage } from './game.ts'

const URLS = {
  tick: tickUrl, clack: clackUrl, burn: burnUrl, phoenix: phoenixUrl,
  stamp: stampUrl, sheet: sheetUrl, fuse: fuseUrl, flare: flareUrl,
  'clink-1100': clink1100Url, 'clink-2200': clink2200Url, 'clink-4400': clink4400Url,
  'ablaze-fire': ablazeFireUrl, 'ablaze-crackle': ablazeCrackleUrl,
} as const
type Clip = keyof typeof URLS
/** Loaded on the first key press. The phoenix, the longest clip, waits for the phoenix round. */
const FIRST: readonly Clip[] = ['tick', 'clack', 'burn']
/** Loaded when Results opens (preloadAchievementSounds), or else the first time one is played. */
const ACHIEVEMENT: readonly Clip[] = ['tick', 'clack', 'stamp', 'sheet', 'fuse', 'flare', 'clink-1100', 'clink-2200', 'clink-4400']
/** Overall level. The clips keep the ad's balance against each other: tick quietest, phoenix loudest. */
const VOLUME = 0.1
/** A clip still loading when it was asked for plays only if it's ready this soon after. */
const LATE_MS = 120

let ctx: AudioContext | null = null
let out: GainNode | null = null
const clips = new Map<Clip, Promise<AudioBuffer>>()
/** What Results' sounds play through, so stopAchievementSounds can hush them all at once. */
let fx: GainNode | null = null
/** Bumped by stopAchievementSounds, so sounds still loading when it's called stay quiet. */
let fxRound = 0

export function soundOn(): boolean { return storage.get('lof:sound') !== 'off' }
export function setSoundOn(on: boolean) {
  storage.set('lof:sound', on ? 'on' : 'off')
  if (!on && ctx) void ctx.suspend() // also cuts off a burn that's still crackling
}

function load(name: Clip) {
  if (!ctx || clips.has(name)) return
  const c = ctx
  const p = fetch(URLS[name]).then((r) => r.arrayBuffer()).then((b) => c.decodeAudioData(b))
  p.catch(() => clips.delete(name)) // try again next time
  clips.set(name, p)
}

/** Call on a key press or tap: browsers only let audio start from one. Loads the clips the first time. */
export function primeSound() {
  if (!soundOn() || typeof AudioContext === 'undefined') return
  if (!ctx) {
    // iOS: an ambient session follows the silent switch and mixes with music that's already playing.
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession
    if (session) session.type = 'ambient'
    ctx = new AudioContext()
    out = ctx.createGain()
    out.gain.value = VOLUME
    out.connect(ctx.destination)
  }
  FIRST.forEach(load)
  if (ctx.state === 'suspended') void ctx.resume()
}

/** Starts loading the phoenix's sound, for when its round opens (only once sound has been primed). */
export function preloadPhoenix() { if (soundOn()) load('phoenix') }

/**
 * Runs `then` once all of `names` have loaded, with how many seconds late that is. Results' sounds
 * (onFx) go through the fx bus and are dropped if stopAchievementSounds was called in the meantime.
 */
function whenLoaded(names: readonly Clip[], then: (c: AudioContext, dest: AudioNode, bufs: AudioBuffer[], late: number) => void, onFx = false) {
  if (!soundOn()) return
  primeSound()
  names.forEach(load)
  const pending = names.map((n) => clips.get(n))
  if (!ctx || !out || pending.some((p) => !p)) return
  const c = ctx, o = out, asked = performance.now(), round = fxRound
  Promise.all(pending as Promise<AudioBuffer>[]).then((bufs) => {
    if (onFx && round !== fxRound) return
    let dest: AudioNode = o
    if (onFx) {
      if (!fx) { fx = c.createGain(); fx.connect(o) }
      dest = fx
    }
    then(c, dest, bufs, (performance.now() - asked) / 1000)
  }, () => {})
}

type Voice = {
  clip: Clip
  /** Seconds after it was asked for. */
  at?: number
  rate?: number
  /** On top of the clip's level in levels.ts. */
  gain?: number
  /** For the mono clinks: placed as the recipe's mix would (equal power, so 0 is centre at -3 dB). */
  pan?: number
}

/** Starts a voice on its time. One that would start more than LATE_MS late (still loading) is skipped. */
function start(c: AudioContext, dest: AudioNode, buf: AudioBuffer, late: number, { clip, at = 0, rate = 1, gain = 1, pan }: Voice) {
  const when = at - late
  if (when < -LATE_MS / 1000) return
  const src = c.createBufferSource()
  src.buffer = buf
  src.playbackRate.value = rate
  let node: AudioNode = src
  const level = (LEVELS[clip] ?? 1) * gain
  if (level !== 1) {
    const g = c.createGain()
    g.gain.value = level
    node = node.connect(g)
  }
  if (pan !== undefined) {
    const p = c.createStereoPanner()
    p.pan.value = pan
    node = node.connect(p)
  }
  node.connect(dest)
  src.start(c.currentTime + Math.max(0, when))
}

/** Plays voices together once their clips have loaded, keeping their times relative to the ask. */
function playVoices(voices: readonly Voice[], onFx = false) {
  const names = [...new Set(voices.map((v) => v.clip))]
  whenLoaded(names, (c, dest, bufs, late) => {
    for (const v of voices) start(c, dest, bufs[names.indexOf(v.clip)], late, v)
  }, onFx)
}

function play(clip: Clip, rate = 1) { playVoices([{ clip, rate }]) }
const playFx = (...voices: Voice[]) => playVoices(voices, true)

/** A letter landing on a tile, pitched a little differently each time so typing never sounds looped. */
export function playTick() { play('tick', 0.92 + Math.random() * 0.16) }

/** Enter: the clack, and the burn when the word put letters in the ashes, burnAfter ms later (as a tile catches). */
export function playSubmit(burned: boolean, burnAfter = 0) {
  play('clack')
  if (!burned) return
  const burn = () => play('burn', 0.97 + Math.random() * 0.06)
  if (burnAfter > 0) setTimeout(burn, burnAfter)
  else burn()
}

/** Raise: the clack, then the ashes catch and the phoenix blooms PHOENIX_BLOOM_MS later. */
export function playPhoenix() {
  play('clack')
  play('phoenix')
}

/** When the phoenix sound blooms, from the tap; the rise animation in app.css peaks on it (synth.mjs PHOENIX_BLOOM). */
export const PHOENIX_BLOOM_MS = 550

// ---------- Results: the score landing and the achievement reveal ----------
// Results owns the timing and calls these as things happen on screen. The recipes and their
// levels are in design/game-sounds/synth.mjs (approved in design/hot-type/boards/sfx.js).

/**
 * Starts loading Results' sounds, for when it opens (only once sound has been primed). Pass
 * ablaze: true when a category will go Ablaze, to load its two recordings (about 1 MB) as well.
 */
export function preloadAchievementSounds({ ablaze = false }: { ablaze?: boolean } = {}) {
  if (!soundOn()) return
  ACHIEVEMENT.forEach(load)
  if (ablaze) { load('ablaze-fire'); load('ablaze-crackle') }
}

/** Starts loading the score landing's sounds (the ticks, the clack, the stamp), for Results opening as a game ends. */
export function preloadLandingSounds() {
  if (!soundOn()) return
  for (const clip of ['tick', 'clack', 'stamp'] as const) load(clip)
}

/** Hushes Results' sounds that are playing or scheduled (a tap that skips ahead, or leaving the page). */
export function stopAchievementSounds() {
  fxRound++
  if (!ctx || !fx) return
  const bus = fx, t = ctx.currentTime
  fx = null
  bus.gain.setValueAtTime(1, t)
  bus.gain.linearRampToValueAtTime(0, t + 0.04)
  setTimeout(() => bus.disconnect(), 100)
}

/** The score counting up, one tick about every 70 ms of the 1.2 s count, climbing in pitch with progress (0–1). */
export function playCountTick(progress: number) {
  playFx({ clip: 'tick', rate: 0.8 + 0.7 * Math.min(1, Math.max(0, progress)), gain: COUNT_TICK })
}

/** The rank name stepping up a rung while the score counts. */
export function playRankClack() { playFx({ clip: 'clack' }) }

/** The rank stamping down once the score has landed. */
export function playStamp() { playFx({ clip: 'stamp' }) }

/** The achievement sheet sliding up. */
export function playSheet() { playFx({ clip: 'sheet' }) }

/**
 * The fuse while the spark runs the ring for `seconds` (about 0.35–0.85). fuse.wav is the recipe for
 * the longest run with its fizz and crackle on separate channels; a shorter run reshapes each layer
 * to that run's own recipe (its swell and decay) and fades out as the spark arrives.
 */
export function playFuse(seconds: number) {
  const run = Math.min(FUSE_RUN, Math.max(0.15, seconds))
  const len = run + 0.1
  const level = LEVELS.fuse ?? 1
  // synth.mjs fuseParts: the fizz swells over the whole clip (sin^1.6) and decays with run/2; the
  // crackle's pops decay with run * 0.6. Each curve is the short recipe over the long one.
  const n = 48
  const fizz: number[] = [], crackle: number[] = []
  for (let i = 0; i < n; i++) {
    const t = Math.max(1e-4, (i / (n - 1)) * len)
    const swell = Math.max(0, Math.sin((Math.PI * t) / len)) / Math.sin((Math.PI * t) / (FUSE_RUN + 0.1))
    fizz.push(level * swell ** 1.6 * Math.exp(-Math.max(0, t - 0.04) * (2 / run - 2 / FUSE_RUN)))
    crackle.push(level * Math.exp(-t * (1 / (0.6 * run) - 1 / (0.6 * FUSE_RUN))))
  }
  fizz[n - 1] = 0
  crackle[n - 1] = 0
  whenLoaded(['fuse'], (c, dest, [buf], late) => {
    if (late > LATE_MS / 1000) return
    const t0 = c.currentTime
    const src = c.createBufferSource()
    src.buffer = buf
    const split = c.createChannelSplitter(2)
    src.connect(split)
    ;([[fizz, -0.1], [crackle, 0.1]] as const).forEach(([curve, pan], ch) => {
      const g = c.createGain()
      g.gain.setValueCurveAtTime(curve, t0, len)
      const p = c.createStereoPanner()
      p.pan.value = pan
      split.connect(g, ch)
      g.connect(p).connect(dest)
    })
    src.start(t0)
    src.stop(t0 + len)
  }, true)
}

// The clinks' ladder, as synth.mjs clinkHz / catchHz: 1.5 semitones a segment from 1100 Hz.
const CLINK_HZ = 1100
const clinkHz = (step: number) => CLINK_HZ * 2 ** ((step * 1.5) / 12)
const catchHz = (step: number) => Math.min(3600, CLINK_HZ * 2 ** ((step * 1.5 + 3) / 12))
const CLINKS = ['clink-1100', 'clink-2200', 'clink-4400'] as const

/** A clink at hz: the clink rendered nearest that pitch (one per octave), re-pitched to it. */
function clink(hz: number, gain: number, pan: number, at = 0): Voice {
  const k = Math.min(2, Math.max(0, Math.round(Math.log2(hz / CLINK_HZ))))
  return { clip: CLINKS[k], rate: hz / (CLINK_HZ * 2 ** k), gain, pan, at }
}

/** The spark passing segment `step` (0-based) of the ring, already earned: each a little higher. */
export function playClink(step: number) { playFx(clink(clinkHz(step), 0.42, Math.sin(step) * 0.3)) }

/** The new segment catching (`step`: its index, the number earned before it): the loudest clink, over a small flare. */
export function playCatch(step: number) { playFx(clink(catchHz(step), 0.5, 0), { clip: 'flare' }) }

/** The tier up's run of clinks, as synth.mjs TIER_RUN and tierVoices. */
const TIER_RUN = { bronze: [1, 1.26, 1.5], silver: [1, 1.26, 1.5, 1.68], gold: [1, 1.26, 1.5, 1.68] }
const TIER_SPAN = 0.7

/**
 * A tier up as segment `step` catches. Bronze, silver and gold: a run of clinks from an octave under
 * the catch note climbing back to it over 0.7 s, ending on one short clink there at 0.72 s (gold adds
 * two quiet sparkles). Ablaze: the two recorded fire layers, the whoosh from 0.22 s and the crackle
 * from 0.35 s (synth.mjs ABLAZE), so the whoosh peaks as the emblem pops.
 */
export function playTierUp(tier: 'bronze' | 'silver' | 'gold' | 'blaze', step: number) {
  if (tier === 'blaze') {
    // Separately, so neither waits on the other's loading.
    playFx({ clip: 'ablaze-fire', at: 0.22 })
    playFx({ clip: 'ablaze-crackle', at: 0.35 })
    return
  }
  const base = catchHz(step) / 2
  const run = TIER_RUN[tier]
  const gap = TIER_SPAN / run.length
  const land = TIER_SPAN + 0.02
  const voices = run.map((r, i) => clink(base * r, 0.34, (i - (run.length - 1) / 2) * 0.12, i * gap))
  voices.push(clink(base * 2, 0.42, 0, land))
  if (tier === 'gold') voices.push(clink(Math.min(5000, base * 2.52), 0.09, 0.3, land + 0.18), clink(Math.min(5600, base * 3), 0.06, -0.3, land + 0.32))
  playFx(...voices)
}
